import { z } from "zod";
import { buildMerchantChatCapabilityIntent, merryChatExtensionsMetadataSchema } from "./chatExtensions";
import type { ChatAddMenuSendContext } from "./ChatAddMenuComposer";

export const pendingHomeIntentKey = "merry.pending-home-intent.v1";
const lifetime = 60 * 60 * 1000;
const intentSchema = z.object({
  schemaVersion: z.literal(1),
  id: z.string().uuid(),
  createdAt: z.number().int().nonnegative(),
  text: z.string().max(16000),
  extensions: merryChatExtensionsMetadataSchema,
  action: z.enum(["send", "resources"]),
  state: z.enum(["queued", "failed", "handed_off"]),
  userId: z.string(),
  organizationId: z.string(),
  conversationId: z.string(),
}).strict();
export type PendingHomeIntent = z.infer<typeof intentSchema>;

export function parsePendingHomeIntent(value: unknown, now = Date.now()) {
  const parsed = intentSchema.safeParse(value);
  if (!parsed.success || parsed.data.createdAt > now || now - parsed.data.createdAt > lifetime) return null;
  return parsed.data;
}

export function readPendingHomeIntent(storage: Storage = sessionStorage) {
  try {
    const raw = storage.getItem(pendingHomeIntentKey);
    if (!raw) return null;
    const intent = parsePendingHomeIntent(JSON.parse(raw));
    if (!intent) storage.removeItem(pendingHomeIntentKey);
    return intent;
  } catch {
    try { storage.removeItem(pendingHomeIntentKey); } catch { /* 浏览器拒绝存储时保持空草稿，不读取半成品。 */ }
    return null;
  }
}

export function writePendingHomeIntent(intent: PendingHomeIntent, storage: Storage = sessionStorage) {
  storage.setItem(pendingHomeIntentKey, JSON.stringify(intentSchema.parse(intent)));
}

export function clearPendingHomeIntent(id?: string, storage: Storage = sessionStorage) {
  try {
    if (!id || readPendingHomeIntent(storage)?.id === id) storage.removeItem(pendingHomeIntentKey);
  } catch { /* 浏览器禁用存储时不阻断新对话或终态处理。 */ }
}

export function createPendingHomeIntent(text: string, context: ChatAddMenuSendContext, action: PendingHomeIntent["action"], owner?: { userId: string; activeOrganizationId: string }) {
  return intentSchema.parse({
    schemaVersion: 1, id: crypto.randomUUID(), createdAt: Date.now(), text,
    extensions: context.extensions ?? [], action, state: "queued",
    userId: owner?.userId ?? "", organizationId: owner?.activeOrganizationId ?? "", conversationId: "",
  });
}

export function pendingHomeIntentContext(intent: PendingHomeIntent): ChatAddMenuSendContext {
  // 登录前只保留安全能力意图；组织资源必须在登录后重新选择/上传。
  const capabilityIntent = buildMerchantChatCapabilityIntent(intent.extensions, []);
  return { ...(capabilityIntent ? { capabilityIntent } : {}), extensions: intent.extensions, resourceIds: [], resourceRefs: [] };
}

export function pendingHomeIntentBelongsTo(intent: PendingHomeIntent, userId: string, organizationId: string) {
  return (!intent.userId || intent.userId === userId) && (!intent.organizationId || intent.organizationId === organizationId);
}

const running = new Map<string, Promise<void>>();
export function runHomeIntentOnce(id: string, action: () => Promise<void>) {
  const existing = running.get(id);
  if (existing) return existing;
  const promise = action().finally(() => { if (running.get(id) === promise) running.delete(id); });
  running.set(id, promise);
  return promise;
}
